import { Position, positionKey } from '../core/GridGeometry.js';
import { GameType, GAME_TYPES, SavedGrid } from '../persistence/types.js';
import { VortexGateManager } from '../games/VortexGateManager.js';

export interface GatePairUsage {
    /** Game types that reference this pair in serialized (current state) */
    gamesUsing: GameType[];
    /** Game types that reference this pair in bestSerialized (win state) */
    gamesUsingInBest: GameType[];
    /** Total number of distinct games affected */
    affectedCount: number;
    /** Number of win states invalidated */
    invalidatedWins: number;
}

/**
 * Check if a serialized game state's gate portion references a specific gate pair.
 * Gate portion format after '|': "A1=B7,C3=D4"
 */
export function stateReferencesGatePair(
    serialized: string,
    serializedPosA: string,
    serializedPosB: string
): boolean {
    const { gatePart } = VortexGateManager.splitSerialized(serialized);
    if (!gatePart) return false;
    return VortexGateManager.parseGatePairs(gatePart).some(
        ([a, b]) => (a === serializedPosA && b === serializedPosB) || (a === serializedPosB && b === serializedPosA)
    );
}

/**
 * Scan all 12 games on a grid for usage of a specific gate pair.
 */
export function getGatePairUsage(
    grid: SavedGrid,
    serializedPosA: string,
    serializedPosB: string
): GatePairUsage {
    const gamesUsing: GameType[] = [];
    const gamesUsingInBest: GameType[] = [];

    for (const gameType of GAME_TYPES) {
        const state = grid.gameStates[gameType];
        if (!state) continue;

        if (state.serialized && stateReferencesGatePair(state.serialized, serializedPosA, serializedPosB)) {
            gamesUsing.push(gameType);
        }
        if (state.bestSerialized && stateReferencesGatePair(state.bestSerialized, serializedPosA, serializedPosB)) {
            gamesUsingInBest.push(gameType);
        }
    }

    const allAffected = new Set([...gamesUsing, ...gamesUsingInBest]);

    return {
        gamesUsing,
        gamesUsingInBest,
        affectedCount: allAffected.size,
        invalidatedWins: gamesUsingInBest.length,
    };
}

/**
 * Check if a bestSerialized state is compatible with the current Hub.
 * All gate pairs in the state must still exist as linked pairs in the Hub.
 */
export function isBestStateCompatible(
    bestSerialized: string,
    hubPairings: Map<string, string>,
    allGates: Set<string>,
    gs: { deserializePosition: (s: string) => Position },
): boolean {
    const { gatePart } = VortexGateManager.splitSerialized(bestSerialized);
    if (!gatePart) return true; // No gates used = always compatible

    for (const [strA, strB] of VortexGateManager.parseGatePairs(gatePart)) {
        try {
            const keyA = positionKey(gs.deserializePosition(strA));
            const keyB = positionKey(gs.deserializePosition(strB));
            // Both gates must exist and be paired to each other
            if (!allGates.has(keyA) || !allGates.has(keyB)) return false;
            if (hubPairings.get(keyA) !== keyB) return false;
        } catch {
            return false;
        }
    }
    return true;
}

/**
 * Check if a serialized state has at least one gate pair still valid in the Hub.
 */
export function hasCompatibleGates(
    serialized: string,
    hubPairings: Map<string, string>,
    allGates: Set<string>,
    gs: { deserializePosition: (s: string) => Position },
): boolean {
    const { gatePart } = VortexGateManager.splitSerialized(serialized);
    if (!gatePart) return false;

    for (const [strA, strB] of VortexGateManager.parseGatePairs(gatePart)) {
        try {
            const keyA = positionKey(gs.deserializePosition(strA));
            const keyB = positionKey(gs.deserializePosition(strB));
            if (allGates.has(keyA) && allGates.has(keyB) && hubPairings.get(keyA) === keyB) {
                return true;
            }
        } catch {
            continue;
        }
    }
    return false;
}
